package com.essntl.features.itinerary.ui.edit_itinerary_service.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import core.dateformat.utils.stringToLocalDateTime
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceState
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceValidation
import com.essntl.uikit.components.expandable.ExpandableTabs
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun ColumnScope.EditItineraryServiceDetails(
    state: EditItineraryServiceState,
    category: CategoryModel?,
    itineraryService: ItineraryServiceModel,
    validation: EditItineraryServiceValidation,
    onItineraryServiceChange: (ItineraryServiceModel) -> Unit,
) {
    val tabList = remember {
        listOf(
            "PO Details",
            "PO History",
        )
    }

    ExpandableTabs(
        tabList = tabList,
        id = itineraryService.id,
        modifier = Modifier
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .fillMaxWidth()
            .weight(1f),
    ) { index ->
        when (index) {
            0 -> {
                Column {
                    PurchaseOrderDetails(
                        category = category,
                        itineraryService = itineraryService,
                        validation = validation,
                        onItineraryServiceChange = onItineraryServiceChange,
                    )
                }
            }

            1 -> {
                LazyColumn(
                    horizontalAlignment = Alignment.Start,
                    contentPadding = PaddingValues(top = 1.spacing),
                    state = rememberLazyListState(),
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(max = 300.dp),
                ) {
                    if (state.waypointLogsList.isLoading)
                        item {
                            EsSpinner()
                        }
                    else if (state.waypointLogsList.isFailure)
                        item {
                            Text(
                                text = "Something went wrong!",
                                style = MaterialTheme.esTypography.p2Medium14,
                            )
                        }
                    else if (state.waypointLogsList.data.orEmpty().isEmpty())
                        item {
                            Text(
                                text = "Nothing to show",
                                style = MaterialTheme.esTypography.p2Medium14,
                            )
                        }
                    else
                        itemsIndexed(
                            state.waypointLogsList
                                .data
                                .orEmpty()
                                .sortedByDescending { stringToLocalDateTime(it.createdAt) },
                        ) { index, w ->
                            WaypointStatusItem(
                                waypointLogs = w,
                                showDivider = index != state.waypointLogsList.data.orEmpty().size - 1,
                            )
                        }
                }
            }
        }
    }
}

@Composable
fun PurchaseOrderDetails(
    category: CategoryModel?,
    itineraryService: ItineraryServiceModel,
    validation: EditItineraryServiceValidation,
    onItineraryServiceChange: (ItineraryServiceModel) -> Unit,
) {

    DateTimeComponent(
        itineraryService = itineraryService,
        validation = validation,
        onItineraryServiceChange = onItineraryServiceChange,
    )
    Spacer(modifier = Modifier.height(height = 1.5.spacing))

    EsInput(
        value = itineraryService.guestCount.let { if (it > 0) "$it" else "" },
        onValueChange = {
            val guestCount = it.toIntOrNull() ?: 0
            onItineraryServiceChange(
                itineraryService.copy(
                    guestCount = guestCount,
                ),
            )
        },
        placeholder = "Number of people participating to the service",
        label = "Count",
        modifier = Modifier.fillMaxWidth(),
    )

    Spacer(modifier = Modifier.height(height = 1.5.spacing))

    // Dynamic fields
    DynamicFields(
        category = category,
        itineraryService = itineraryService,
        onItineraryServiceChange = onItineraryServiceChange,
    )
    Spacer(modifier = Modifier.height(height = 1.5.spacing))

    EsInput(
        value = itineraryService.specialNotes ?: "",
        onValueChange = {
            onItineraryServiceChange(
                itineraryService.copy(
                    specialNotes = it,
                ),
            )
        },
        placeholder = "Special notes for this service",
        label = "Special notes",
        singleLine = false,
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(height = 3.spacing))

}